import { ChangeEvent, useMemo, useRef } from "react";
import classNames from "classnames";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ClickableIcon } from "../clickable-icon/clickable-icon";
import { IdwFormLabelWithIconContainer } from "../idw-form-label-with-icon-container/idw-form-label-with-icon-container";
import styles from "./file-input-form-group.less";

type FileInputFormGroupProps = {
    controlId: string;
    labelWidth?: LabelWidthTypes;
    className?: string;
    automationId?: string;
    ariaLabel?: string;
    label: string;
    value: string;
    accept?: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onClear?: (value: string) => void;
    layout?: LayoutTypes;
    disabled: boolean;
    isRequired: boolean;
    autoComplete?: "on" | "off";
    uploadInProgress?: boolean;
    afterLabelTooltipText?: string;
    afterLabelIcon?: IconDefinition;
    afterLabelIconDisabled?: boolean;
    afterLabelIconOnClick?: () => void;
    afterInputIcon?: IconDefinition;
    afterInputIconDisabled?: boolean;
    afterInputIconOnClick?: () => void;
    rightAlignedLabelElement?: React.ReactNode;
};

export const FileInputFormGroup = (props: FileInputFormGroupProps) => {
    const {
        controlId,
        labelWidth,
        className,
        automationId,
        ariaLabel,
        label,
        value,
        accept,
        onChange,
        onClear,
        layout,
        disabled,
        isRequired,
        autoComplete,
        uploadInProgress,
        afterLabelIcon,
        afterLabelIconDisabled,
        afterLabelIconOnClick,
        afterLabelTooltipText,
        afterInputIcon,
        afterInputIconDisabled,
        afterInputIconOnClick,
        rightAlignedLabelElement
    } = props;

    const inputRef = useRef(null);

    const onChooseFileClick = () => {
        if (inputRef.current) {
            inputRef.current.value = null;
            inputRef.current.click();
        }
    };

    const clearFile = () => {
        if (inputRef.current) {
            inputRef.current.value = null;
            onClear("");
        }
    }

    const formGroupClassName = useMemo(() => {
        return layout === LayoutTypes.COLUMN ? styles.fileInputFormGroupColumn : styles.fileInputFormGroupRow;
    }, [layout]);

    const fileName = useMemo(() => {
        if (value) {
            const fileParts = value.split("/");
            return fileParts[fileParts.length - 1];
        }
        return "";
    }, [value]);

    return (
        <Form.Group controlId={controlId} className={classNames(formGroupClassName, className)}>
            {uploadInProgress && (
                <div className={styles.uploadingDiv}>
                    <Spinner className={styles.uploadingSpinner} animation={"border"} />
                </div>
            )}
            <IdwFormLabelWithIconContainer
                controlId={controlId}
                labelWidth={labelWidth}
                layout={layout}
                label={label}
                isRequired={isRequired}
                afterLabelTooltipText={afterLabelTooltipText}
                afterLabelIcon={afterLabelIcon}
                afterLabelIconDisabled={afterLabelIconDisabled}
                afterLabelIconOnClick={afterLabelIconOnClick}
                rightAlignedLabelElement={rightAlignedLabelElement}
            />
            <div className={styles.inputContainer}>
                {!disabled && (
                    <Button id={`${controlId}-choose-file-button`} disabled={uploadInProgress} className={styles.chooseFileButton} onClick={onChooseFileClick} variant={"outline-primary"}>{resources.common.buttons.chooseFile.label}</Button>
                )}
                <Form.Control
                    as="input"
                    required={isRequired}
                    automation-id={automationId}
                    aria-label={ariaLabel}
                    type={"text"}
                    onClick={onChooseFileClick}
                    autoComplete={autoComplete || "off"}
                    value={fileName}
                    placeholder={resources.common.inputs.fileInput.placeholder}
                    disabled={disabled || uploadInProgress}
                    readOnly={true}
                />
                {!disabled && onClear && fileName !== "" && (
                    <Button id={`${controlId}-clear-file-button`} disabled={uploadInProgress} className={styles.clearFileButton} onClick={clearFile} variant={"outline-primary"}>{resources.common.buttons.clear.label}</Button>
                )}
                {afterInputIcon && afterInputIconOnClick && (
                    <div>
                        <ClickableIcon controlId={controlId} disabled={afterInputIconDisabled} icon={afterInputIcon} onClick={afterInputIconOnClick} className={styles.afterInputIcon} />
                    </div>
                )}
            </div>
            <input id={`${controlId}-file-input`} className={styles.fileInput} type="file" ref={inputRef} accept={accept} onChange={onChange} />
        </Form.Group>
    );
};